import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState, type FormEvent } from 'react';

import {
  addMyUnavailability,
  addUnavailability,
  askMyLeave,
  cancelLeave,
  decideLeave,
  leaveTypesQuery,
  myLeaveTypesQuery,
  REASONS,
  recordLeave,
  retireUnavailability,
  type Decided,
  type TimeOffEntry,
  type UnavailabilityReason,
  type When,
} from '../api/time-off.ts';
import { Button } from '../components/button.tsx';
import { Field } from '../components/field.tsx';
import { Popover } from '../components/popover.tsx';

// CB-REQ CRW-02 at v1.9106, crew part 4D-2: unavailability and leave on the roster. Entered by the
// office (a person's row, drawn on), opened in place from the block behind the row, and decided there
// with leave.approve. The two words are the operator's: Unavailable and Leave, never one over both.

export type TimeOffKind = 'unavailable' | 'leave';

/** Wall-clock in the roster's zone, as a drawn window gives it. */
interface Window {
  readonly start: string;
  readonly end: string;
}

/** The last day a drawn window touches: one ending at midnight ends the day before. */
function lastDay(w: Window): string {
  const day = w.end.slice(0, 10);
  return w.end.slice(11) === '00:00' && day > w.start.slice(0, 10)
    ? new Date(Date.parse(`${day}T00:00Z`) - 86_400_000).toISOString().slice(0, 10)
    : day;
}

/**
 * A new unavailability or leave: the office's for one person (from a window drawn on their row, or
 * their crew record), or, with no crewMemberId, the person's own on My shifts (crew part 4D-3).
 */
export function TimeOffForm({
  kind,
  crewMemberId,
  drawn,
  onDone,
}: {
  kind: TimeOffKind;
  crewMemberId?: string | undefined;
  drawn: Window;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const own = crewMemberId === undefined;
  const types = useQuery({
    ...(own ? myLeaveTypesQuery : leaveTypesQuery),
    enabled: kind === 'leave',
  });
  const [wholeDays, setWholeDays] = useState(true);
  const [from, setFrom] = useState(drawn.start.slice(0, 10));
  const [to, setTo] = useState(lastDay(drawn));
  const [start, setStart] = useState(drawn.start);
  const [end, setEnd] = useState(drawn.end);
  const [reason, setReason] = useState<UnavailabilityReason>('other_work');
  const [weekly, setWeekly] = useState(false);
  const [leaveTypeId, setLeaveTypeId] = useState('');
  const [note, setNote] = useState('');
  const [problem, setProblem] = useState<string>();
  const ids = { reason: useId(), type: useId() };

  const save = useMutation({
    mutationFn: async (when: When): Promise<void> => {
      const unavailable = { reason, when, note: note.trim() || null, repeatsWeekly: weekly };
      const leave = {
        leaveTypeId: leaveTypeId || types.data?.[0]?.id || '',
        when,
        note: note.trim() || null,
      };
      if (own) {
        await (kind === 'unavailable' ? addMyUnavailability(unavailable) : askMyLeave(leave));
      } else {
        await (kind === 'unavailable'
          ? addUnavailability(crewMemberId, unavailable)
          : recordLeave(crewMemberId, leave));
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['roster'] });
      await queryClient.invalidateQueries({ queryKey: own ? ['my'] : ['crew', crewMemberId] });
      onDone();
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (wholeDays ? !from || !to || to < from : !start || !end || end <= start) {
      return setProblem(
        wholeDays ? 'The last day is on or after the first.' : 'It ends after it starts.',
      );
    }
    setProblem(undefined);
    save.mutate(wholeDays ? { days: { from, to } } : { times: { start, end } });
  }

  return (
    <form className="flex flex-col gap-3" onSubmit={submit} noValidate>
      {kind === 'unavailable' ? (
        <div className="flex flex-col gap-1.5">
          <label htmlFor={ids.reason} className="text-xs text-ink-soft">
            Reason
          </label>
          <select
            id={ids.reason}
            className="input"
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value as UnavailabilityReason)}
          >
            {Object.entries(REASONS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          <label htmlFor={ids.type} className="text-xs text-ink-soft">
            Type
          </label>
          <select
            id={ids.type}
            className="input"
            value={leaveTypeId || types.data?.[0]?.id || ''}
            onChange={(e) => setLeaveTypeId(e.currentTarget.value)}
          >
            {(types.data ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>
      )}
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={wholeDays}
          onChange={(e) => setWholeDays(e.currentTarget.checked)}
        />
        Whole days
      </label>
      <div className="grid grid-cols-2 gap-3">
        {wholeDays ? (
          <>
            <Field
              label="From"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.currentTarget.value)}
            />
            <Field
              label="To"
              type="date"
              value={to}
              onChange={(e) => setTo(e.currentTarget.value)}
            />
          </>
        ) : (
          <>
            <Field
              label="Starts"
              type="datetime-local"
              value={start}
              onChange={(e) => setStart(e.currentTarget.value)}
            />
            <Field
              label="Ends"
              type="datetime-local"
              value={end}
              onChange={(e) => setEnd(e.currentTarget.value)}
            />
          </>
        )}
      </div>
      {kind === 'unavailable' ? (
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={weekly}
            onChange={(e) => setWeekly(e.currentTarget.checked)}
          />
          Every week
        </label>
      ) : null}
      <Field label="Note" value={note} onChange={(e) => setNote(e.currentTarget.value)} />
      {problem || save.isError ? (
        <p role="alert" className="alert">
          {problem ?? save.error?.message}
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button type="submit" disabled={save.isPending}>
          {own && kind === 'leave' ? 'Ask' : 'Add'}
        </Button>
        <Button type="button" variant="quiet" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

const shortDate = (instant: string, zone: string) =>
  new Intl.DateTimeFormat('en-AU', { timeZone: zone, day: 'numeric', month: 'short' }).format(
    new Date(instant),
  );
const shortTime = (instant: string, zone: string) =>
  new Intl.DateTimeFormat('en-AU', {
    timeZone: zone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(instant));

/** "4 Apr", "4 – 6 Apr", or "4 Apr 09:00 – 14:00": whole days read as days. */
export function timeOffWhen(entry: TimeOffEntry, zone: string): string {
  const startTime = shortTime(entry.startsAt, zone);
  const endTime = shortTime(entry.endsAt, zone);
  if (startTime === '00:00' && endTime === '00:00') {
    const first = shortDate(entry.startsAt, zone);
    const last = shortDate(new Date(Date.parse(entry.endsAt) - 1).toISOString(), zone);
    return first === last ? first : `${first} – ${last}`;
  }
  return `${shortDate(entry.startsAt, zone)} ${startTime} – ${endTime}`;
}

export const timeOffTitle = (entry: TimeOffEntry) =>
  `${entry.kind === 'unavailable' ? 'Unavailable' : 'Leave'} · ${entry.label}`;

/** A block behind a row, opened in place: remove it, or decide or cancel the leave. */
export function TimeOffPopover({
  entry,
  name,
  zone,
  mayManage,
  mayApprove,
  onClose,
}: {
  entry: TimeOffEntry;
  name: string;
  zone: string;
  mayManage: boolean;
  mayApprove: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [decided, setDecided] = useState<Decided | null>(null);
  const act = useMutation({
    mutationFn: async (what: 'remove' | 'approve' | 'decline' | 'cancel') => {
      if (what === 'remove') await retireUnavailability(entry.id);
      else if (what === 'cancel') await cancelLeave(entry.id);
      else return decideLeave(entry.id, what);
      return null;
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['roster'] });
      await queryClient.invalidateQueries({ queryKey: ['leave'] });
      // Approving over shifts they already hold says so; they stay, for the office.
      if (result && result.clashingShifts.length > 0) setDecided(result);
      else onClose();
    },
  });
  const pendingLeave = entry.kind === 'leave' && entry.pending;
  return (
    <Popover
      title={timeOffTitle(entry)}
      subtitle={`${name} · ${timeOffWhen(entry, zone)}`}
      tag={pendingLeave ? <span className="tag tag-warn">Not yet approved</span> : null}
      onClose={onClose}
      footer={
        decided ? (
          <Button onClick={onClose}>Done</Button>
        ) : (
          <>
            {pendingLeave && mayApprove ? (
              <>
                <Button disabled={act.isPending} onClick={() => act.mutate('approve')}>
                  Approve
                </Button>
                <Button
                  variant="quiet"
                  disabled={act.isPending}
                  onClick={() => act.mutate('decline')}
                >
                  Decline
                </Button>
              </>
            ) : null}
            {entry.kind === 'leave' && mayManage ? (
              <Button variant="quiet" disabled={act.isPending} onClick={() => act.mutate('cancel')}>
                Cancel leave
              </Button>
            ) : null}
            {entry.kind === 'unavailable' && mayManage ? (
              <Button variant="quiet" disabled={act.isPending} onClick={() => act.mutate('remove')}>
                Remove
              </Button>
            ) : null}
          </>
        )
      }
    >
      {decided ? (
        <div role="status" className="flex flex-col gap-1 text-sm">
          <p className="font-semibold">Approved. Still booked:</p>
          <ul>
            {decided.clashingShifts.map((s) => (
              <li key={s.id}>
                {timeOffWhen({ ...entry, startsAt: s.startsAt, endsAt: s.endsAt }, zone)}
                {s.job ? ` · J-${s.job.number} ${s.job.name}` : ''}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {act.isError ? (
        <p role="alert" className="alert">
          {act.error.message}
        </p>
      ) : null}
    </Popover>
  );
}
